/** @jsxImportSource react */
import { useState } from 'react'
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import { format } from 'date-fns'
import { useSelector } from 'react-redux'
import { getErrorMessage } from '../../utils/apiErrors'
import ConnectCalendarPrompt from './ConnectCalendarPrompt'

const time = (iso) => format(new Date(iso), 'HH:mm')

/**
 * Under the time of a meeting being planned: who's busy then (CRM meetings and connected Google /
 * Outlook calendars; other people's events show only as busy), and free times to pick instead.
 * {@code query}: the availability query; {@code onPick(isoStart)} moves the meeting there.
 */
const AvailabilityPanel = ({ query, onPick }) => {
  const myId = useSelector((state) => state.auth.user?.userId)
  const [suggesting, setSuggesting] = useState(false)
  const a = query.data
  if (query.error) return <Typography variant="caption" color="text.secondary">Couldn’t check calendars: {getErrorMessage(query.error)}</Typography>
  if (!a) {
    return query.isFetching
      ? <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={14} /><Typography variant="caption" color="text.secondary">Checking calendars…</Typography></Stack>
      : null
  }
  const who = (u) => (u.id === myId ? 'You' : u.title)
  const alone = a.people.length === 1
  const unchecked = a.people.filter((p) => !p.calendarConnected).map((p) => who(p.user))
  const errors = a.people.filter((p) => p.calendarError)

  return (
    <Box>
      {a.conflicts.length > 0 ? (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Busy at this time</Typography>
          {a.conflicts.map((c, i) => (
            <Typography key={i} variant="body2">
              {who(c.user)}: {c.allDay ? 'all day' : `${time(c.start)}–${time(c.end)}`}{c.title ? ` · ${c.title}` : ''}
              {c.source !== 'CRM' && <Typography component="span" variant="caption" color="text.secondary"> ({c.source === 'GOOGLE' ? 'Google' : 'Outlook'} calendar)</Typography>}
            </Typography>
          ))}
        </Alert>
      ) : (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 18 }} />
          <Typography variant="body2" color="success.main">{alone && a.people[0].user.id === myId ? 'You’re free then' : 'Everyone is free then'}</Typography>
          {query.isFetching && <CircularProgress size={12} />}
        </Stack>
      )}
      {unchecked.length > 0 && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
          Only CRM meetings checked for {unchecked.join(', ')} (no Google or Outlook calendar connected).
        </Typography>
      )}
      {errors.map((p) => <Typography key={p.user.id} variant="caption" color="warning.main" component="p">{p.calendarError}</Typography>)}
      {a.people.some((p) => p.user.id === myId && !p.calendarConnected) && <ConnectCalendarPrompt variant="inline" />}
      <Button size="small" startIcon={<EventAvailableOutlinedIcon />} onClick={() => setSuggesting((v) => !v)} sx={{ mt: 0.5, px: 0.5 }}>
        {suggesting ? 'Hide suggested times' : 'Suggest times'}
      </Button>
      {suggesting && (
        <Box sx={{ mt: 0.5 }}>
          {a.suggestions.length ? (
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              {a.suggestions.map((s) => (
                <Chip key={s.start} size="small" variant="outlined" color="primary" clickable label={format(new Date(s.start), 'EEE d MMM, HH:mm')}
                  onClick={() => { onPick(s.start); setSuggesting(false) }} />
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">No time when {alone ? 'you’re' : 'everyone is'} free in the next 7 days.</Typography>
          )}
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
            Free for {alone ? 'you' : 'everyone invited from your team'}, {a.workingHours === 'any time, every day' ? 'at any time' : `within working hours (${a.workingHours})`}. Guests outside your team aren’t checked.
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default AvailabilityPanel
